import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getFileViewUrl } from "./sharepointApi";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"];

const extensionOf = (name) => name?.split(".").pop()?.toLowerCase() || "";

/**
 * Shows a file's actual content inline in the explorer's right pane -
 * fetched through the same /sharepoint/file proxy the folder browser used
 * to just open in a new tab (see sharepoint.md - it exists specifically so
 * files can be embedded without SharePoint's own login/X-Frame-Options
 * getting in the way). PDFs get an <iframe>, images an <img>; anything
 * else falls back to an "open" link since there's nothing sensible to
 * embed for it.
 */
export default function FilePreviewPanel({ file, onBack }) {
  const [imageFailed, setImageFailed] = useState(false);
  if (!file) return null;

  const ext = extensionOf(file.name);
  const isPdf = ext === "pdf";
  const isImage = IMAGE_EXTENSIONS.includes(ext);
  const viewUrl = getFileViewUrl(file.webUrl);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
          {file.name}
        </Typography>
        <Button
          size="small"
          endIcon={<OpenInNewIcon />}
          onClick={() => window.open(viewUrl, "_blank", "noopener,noreferrer")}
        >
          Open in new tab
        </Button>
      </Stack>

      <Box sx={{ flexGrow: 1, minHeight: 0, bgcolor: "action.hover" }}>
        {isPdf ? (
          <iframe
            src={viewUrl}
            title={file.name}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        ) : isImage && !imageFailed ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <img
              src={viewUrl}
              alt={file.name}
              onError={() => setImageFailed(true)}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {imageFailed
                ? "Couldn't load a preview for this file."
                : "Preview isn't available for this file type."}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              onClick={() => window.open(viewUrl, "_blank", "noopener,noreferrer")}
            >
              Open {file.name}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
